import { useTimeZone } from '@/auth/useTimeZone.js';
import { InfoPage } from '@/components/info-page.js';
import { CoachSessionsTable } from '@/components/session/coach-sessions-table.js';
import { CoachSessionsTableColumnId } from '@/components/session/sessions-table-columns.js';
import { BaseEntityTableGroupKey, EntityTableGroupBy } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { EmptyArray, getCoachPortalPastSessions, getCoachPortalUpcomingSessions } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { CoachSessionListRouteConstraint } from '@/lib/my-classes-utils.js';
import { RegisteredRouteTree, RoutesWithValidConstraint, useNavigateSearchState } from '@/lib/routing.js';
import { useSorting } from '@/lib/sorting.js';
import { instantToDate } from '@airreading/common';
import {
  CoachPortalSession,
  CoachSessionRemarkV2,
  GetCoachSessionsOptions,
  GetCoachSessionsSortableColumn,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { RouteById } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';

export type CoachSessionListCompatibleRouteId = RoutesWithValidConstraint<CoachSessionListRouteConstraint>;

export interface CoachSessionListProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  route: RouteById<RegisteredRouteTree, CoachSessionListCompatibleRouteId>;
  title: string;
  startDate: Temporal.Instant;
  endDate?: Temporal.Instant;
  hideColumnIds?: Array<CoachSessionsTableColumnId>;
  showCanceled?: boolean;
  from: 'upcoming' | 'past';
  defaultSessionRemarksV2?: Array<CoachSessionRemarkV2>;
  groupBy?: EntityTableGroupBy<CoachPortalSession, GroupKey>;
}

const makeSorting = (
  sortBy: CoachSessionListRouteConstraint['sortBy'],
  from: CoachSessionListProps['from'],
): CoachSessionListRouteConstraint['sortBy'] => {
  return sortBy ? sortBy : from === 'upcoming' ? ['startInstant-asc'] : ['startInstant-desc'];
};

export function CoachSessionList<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  route,
  title,
  startDate,
  endDate,
  hideColumnIds = [],
  showCanceled = false,
  from,
  defaultSessionRemarksV2,
  groupBy,
}: CoachSessionListProps<GroupKey>) {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery], setNameQuery],
      startOnOrAfter: [[rawStartOnOrAfter], setStartOnOrAfter],
      endOnOrBefore: [[rawEndOnOrBefore], setEndOnOrBefore],
    },
    resetFilters,
  } = usePaginatedFilters(route.id, ['query', 'startOnOrAfter', 'endOnOrBefore', 'sessionRemarksV2']);
  const { timeZone } = useTimeZone();
  const [sortBy, didChangeSortBy] = useNavigateSearchState(route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetCoachSessionsSortableColumn>([sortBy, didChangeSortBy]);

  const startOnOrAfter = startDate.toString();
  const endOnOrBefore = endDate?.toString();
  const fromDate = instantToDate(startDate);
  const toDate = endDate ? instantToDate(endDate) : undefined;

  const stableNowInstant = useMemo(() => Temporal.Now.instant().toString(), []);

  const startOnOrAfterInstant = rawStartOnOrAfter
    ? Temporal.PlainDate.from(rawStartOnOrAfter).toZonedDateTime(timeZone).startOfDay().toInstant().toString()
    : startOnOrAfter;

  const endOnOrBeforeInstant = rawEndOnOrBefore
    ? Temporal.PlainDate.from(rawEndOnOrBefore)
        .toZonedDateTime(timeZone)
        .add({ days: 1 })
        .startOfDay()
        .subtract({ milliseconds: 1 })
        .toInstant()
        .toString()
    : endOnOrBefore;

  const requestQuery = {
    pageIndex,
    pageSize,
    startOnOrAfter: startOnOrAfterInstant,
    ...(endOnOrBeforeInstant && { endOnOrBefore: endOnOrBeforeInstant }),
    ...(from === 'upcoming' && { endOnOrAfter: stableNowInstant }),
    query: nameQuery,
    showCanceled: showCanceled.toString(),
    sortBy: makeSorting(sortBy, from),
    sessionRemarksV2: defaultSessionRemarksV2?.join(','),
    includeSessionsWithoutSessionRemarksV2: 'true',
  } satisfies GetCoachSessionsOptions;

  const coachPortalSessionsQuery =
    from === 'upcoming' ? getCoachPortalUpcomingSessions(requestQuery) : getCoachPortalPastSessions(requestQuery);
  const { data: sessionsResp, isPending: isLoadingSessions, error, refetch } = useQuery(coachPortalSessionsQuery);

  useErrorToast(error, 'Error loading classes. Please try again.');

  const sessions = sessionsResp?.coachSessions || EmptyArray;

  const tablePrefix = from === 'past' ? 'sessions-past' : 'sessions-upcoming';
  const pageTestIdPrefix = from === 'past' ? 'sessions-past' : 'sessions-upcoming';

  const filterLeftComponents = useMemo(
    () => (
      <div className="flex w-full min-w-0 flex-col gap-2 md:flex-row md:items-center md:gap-0 md:space-x-2">
        <TableFilterInputField
          className="max-md:w-full max-md:max-w-none"
          placeholder="Filter by student name"
          data-testid={`${pageTestIdPrefix}-filter-input-student-name`}
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <div className="flex w-full min-w-0 items-center gap-2">
          <TableFilterPlainDateRangePicker
            calendarProps={{ fromDate, toDate }}
            fromPlainDateString={rawStartOnOrAfter}
            toPlainDateString={rawEndOnOrBefore}
            onRangeSelect={(range) => {
              setStartOnOrAfter(range.fromPlainDateString);
              setEndOnOrBefore(range.toPlainDateString);
            }}
            placeholder="Filter by date range"
            buttonTestId={`${pageTestIdPrefix}-filter-button-date-range`}
          />
          <TableFilterResetButton show onClick={resetFilters} data-testid={`${pageTestIdPrefix}-filter-button-reset`} />
        </div>
      </div>
    ),
    [
      rawStartOnOrAfter,
      rawEndOnOrBefore,
      nameQuery,
      setNameQuery,
      setStartOnOrAfter,
      setEndOnOrBefore,
      resetFilters,
      fromDate,
      toDate,
      pageTestIdPrefix,
    ],
  );

  return (
    <InfoPage headerText={title} data-testid={`${pageTestIdPrefix}-page`}>
      <CoachSessionsTable
        sessions={sessions}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        filterBarClassName="max-md:items-stretch"
        filterLeftClassName="max-md:w-full max-md:items-stretch max-md:space-x-0"
        isLoading={isLoadingSessions}
        hasError={!!error}
        tableHookProps={{
          rowCount: sessionsResp?.total || 0,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            sorting,
          },
          ...tableSortingOptions,
          onPaginationChange,
        }}
        hideColumnIds={hideColumnIds}
        tableTestId={`${tablePrefix}-table`}
        tablePrefix={tablePrefix}
        cellTestId={(_, columnId, rowIndex) => {
          if (columnId === 'join' || columnId === 'action') {
            return undefined;
          }
          const columnIdToField: Record<string, string> = {
            startInstant: 'time',
            students: 'students',
            lesson: 'lesson',
            class_status: 'class-status',
            feedback: 'feedback',
            status: 'status',
            school: 'school',
            duration: 'duration',
            note: 'note',
          };
          const field = columnIdToField[columnId] || columnId;
          return `${tablePrefix}-table-row-cell-${field}-${rowIndex + 1}`;
        }}
        groupBy={groupBy}
      />
    </InfoPage>
  );
}
